// Selection styling for the list items in the OS and platform columns. The two
// columns sit on different surfaces (white/gray-700 and gray-50/gray-800), so
// they need their own hover shades. `bg-blue-100` doubles as the marker class
// the selection getters query for, so it has to stay in the selected sets.
const OS_ITEM_HOVER = ['rounded', 'hover:bg-gray-100', 'dark:hover:bg-gray-600']
const PLATFORM_ITEM_HOVER = ['rounded', 'hover:bg-gray-200', 'dark:hover:bg-gray-700']
const ITEM_SELECTED = ['bg-blue-100', 'dark:bg-blue-900', 'text-blue-900', 'dark:text-blue-100', 'rounded']
const BUTTON_SELECTED = ['bg-blue-100', 'dark:bg-blue-900', 'text-blue-800', 'dark:text-blue-100']

export default class EnvironmentPicker {
	constructor(containerId, initialSelection = {}, apiUrl) {
		this.container = document.getElementById(containerId)
		this.data = null
		this.selectedOS = null
		this.selectedPlatform = null
		this.selectedBrowser = null
		this.selectedVersion = null
		this.clickHandler = null
		this.apiUrl = apiUrl || '/support/picker/web-automate.json'

		// Set default selection
		const defaultSelection = {
			osFamily: initialSelection.osFamily || 'windows',
			osName: initialSelection.osName || 'win11',
			browserName: initialSelection.browserName || 'chrome',
			browserVersion: initialSelection.browserVersion || null, // null means use first version
			deviceName: initialSelection.deviceName || null,
			deviceVersion: initialSelection.deviceVersion || null
		}

		this.init(defaultSelection)
	}

	async init(defaultSelection) {
		if (!window.tbEnvironments && !window.tbEnvFetching) {
			window.tbEnvFetching = true
			await this.loadEnvironments()
		} else {
			this.data = window.tbEnvironments
		}
		this.render()
		this.attachEventListeners()
		this.applyDefaultSelection(defaultSelection)
	}

	show() {
		this.container.classList.remove('hidden')
	}

	hide () {
		this.container.classList.add('hidden')
	}

	toggle () {
		this.container.classList.toggle('hidden')
	}

	async loadEnvironments() {
		try {
			const response = await fetch(this.apiUrl)
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`)
			}
			window.tbEnvironments = this.data = await response.json()
		} catch (error) {
			console.error('Failed to load environments:', error)
			this.showError('Failed to load environment data')
		}
	}

	showError(message) {
		this.container.innerHTML = `
      <div class="flex items-center justify-center p-8 text-red-600">
        <div class="text-center">
          <p class="text-lg font-medium">Error</p>
          <p class="text-sm">${message}</p>
        </div>
      </div>
    `
	}

	render() {
		if (!this.data) {
			this.showError('No environment data available')
			return
		}

		this.container.innerHTML = `
      <div class="inline-flex not-prose rounded-lg shadow-md ring-1 ring-gray-200 dark:ring-gray-600 overflow-hidden text-sm text-gray-900 dark:text-gray-100">
        <!-- OS Family Sidebar -->
        <div class="w-40 p-2 bg-white dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600">
          <ul id="os-list" class="space-y-1">
            ${this.renderOSList()}
          </ul>
        </div>

        <!-- OS Platforms -->
        <div id="platforms-panel" class="w-48 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-600 p-2" style="display: none;">
          <ul id="platforms-list" class="space-y-1">
          </ul>
        </div>

        <!-- Browsers Panel -->
        <div id="browsers-panel" class="space-y-6 overflow-y-auto max-h-[400px] bg-white dark:bg-gray-700" style="display: none;">
          <div id="browsers-content"></div>
        </div>
      </div>
    `
	}

	renderOSList() {
		const desktopOSFamilies = Object.keys(this.data.desktop || {})
		const mobileOSFamilies = Object.keys(this.data.mobile || {})

		const renderOSItem = (osFamily) => {
			const displayName = this.getOSDisplayName(osFamily)
			const iconPath = this.getOSIcon(osFamily)

			return `
        <li class="flex items-center justify-between p-2 ${OS_ITEM_HOVER.join(' ')} cursor-pointer"
            data-os-family="${osFamily}">
          <div class="flex items-center space-x-2">
            <img src="${iconPath}" class="w-5 h-5 ${this.getOSIconClass(osFamily)}" alt="${displayName}" />
            <span>${displayName}</span>
          </div>
          <span class="right-arrow hidden">→</span>
        </li>
      `
		}

		const desktopItems = desktopOSFamilies.map(renderOSItem).join('')
		const mobileItems = mobileOSFamilies.map(renderOSItem).join('')

		// Add divider between desktop and mobile if both exist
		const divider = (desktopItems && mobileItems) ? '<li class="border-t border-gray-200 dark:border-gray-600 my-2"></li>' : ''

		return desktopItems + divider + mobileItems
	}

	getOSDisplayName(osFamily) {
		const names = {
			'windows': 'Windows',
			'macos': 'macOS',
			'linux': 'Linux',
			'ios': 'iOS',
			'android': 'Android'
		}
		return names[osFamily] || osFamily
	}

	getOSIcon(osFamily) {
		const icons = {
			'windows': '/assets/environments/svg/windows11.svg',
			'macos': '/assets/environments/svg/ios.svg',
			'linux': '/assets/environments/svg/linux.svg',
			'ios': '/assets/environments/svg/ios.svg',
			'android': '/assets/environments/svg/android.svg'
		}
		return icons[osFamily] || '/assets/environments/svg/windows11.svg'
	}

	getOSIconClass(osFamily) {
		// The Apple mark is solid black artwork, so it disappears on the dark
		// surface. The other icons are full colour and must not be inverted.
		return (osFamily === 'macos' || osFamily === 'ios') ? 'dark:invert' : ''
	}

	renderPlatforms(osFamily) {
		// Check if it's a desktop platform
		if (this.data.desktop && this.data.desktop[osFamily]) {
			const platforms = this.data.desktop[osFamily]
			return Object.keys(platforms).map(platformKey => {
				const platform = platforms[platformKey]
				const isSelected = this.selectedPlatform === platformKey

				return `
          <li class="flex items-center justify-between p-2 ${isSelected ? ITEM_SELECTED.join(' ') : PLATFORM_ITEM_HOVER.join(' ')} cursor-pointer"
              data-platform="${platformKey}">
            <span>${platform.name}</span>
            <span class="right-arrow hidden">→</span>
          </li>
        `
			}).join('')
		}

		// Check if it's a mobile platform
		if (this.data.mobile && this.data.mobile[osFamily]) {
			const mobileData = this.data.mobile[osFamily]
			const platforms = []

			// Add simulators/emulators if available
			if (osFamily === 'ios' && mobileData.simulators) {
				platforms.push({ key: 'simulators', name: 'Simulators', devices: mobileData.simulators })
			}
			if (osFamily === 'android' && mobileData.emulators) {
				platforms.push({ key: 'emulators', name: 'Emulators', devices: mobileData.emulators })
			}

			// Add physical devices if available
			if (mobileData.physical) {
				platforms.push({ key: 'physical', name: 'Physical Devices', devices: mobileData.physical })
			}

			return platforms.map(platform => {
				const isSelected = this.selectedPlatform === platform.key
				return `
          <li class="flex items-center justify-between p-2 ${isSelected ? ITEM_SELECTED.join(' ') : PLATFORM_ITEM_HOVER.join(' ')} cursor-pointer"
              data-platform="${platform.key}">
            <span>${platform.name}</span>
            <span class="right-arrow hidden">→</span>
          </li>
        `
			}).join('')
		}

		return ''
	}

	renderBrowsers(osFamily, platformKey) {
		// Handle desktop browsers
		if (this.data.desktop && this.data.desktop[osFamily]) {
			const platform = this.data.desktop[osFamily][platformKey]
			if (!platform || !platform.browsers) return ''

			return Object.keys(platform.browsers).map(browserKey => {
				const browser = platform.browsers[browserKey]
				const versions = browser.versions || []
				const visibleVersions = versions.slice(0, 5)
				const remainingCount = versions.length - visibleVersions.length

				return `
          <div class="browser-section mb-3" data-browser="${browserKey}">
            <div class="sticky top-0 z-10 flex justify-between items-center mb-2 bg-gray-100 dark:bg-gray-800 p-2">
              <div class="flex items-center space-x-2">
                <img src="${browser.icon}" class="w-5 h-5" alt="${browser.name}" />
                <span class="font-medium">${browser.name}</span>
              </div>
              ${remainingCount > 0 ? `<span class="text-sm text-blue-600 cursor-pointer show-more" data-browser="${browserKey}" data-expanded="false">${remainingCount} more ↓</span>` : ''}
            </div>
            <div class="versions-container grid gap-2 grid-cols-5" data-browser="${browserKey}">
              ${this.renderVersions(browserKey, visibleVersions)}
            </div>
          </div>
        `
			}).join('')
		}

		// Handle mobile devices
		if (this.data.mobile && this.data.mobile[osFamily]) {
			return this.renderMobileDevices(osFamily, platformKey)
		}

		return ''
	}

	renderMobileDevices(osFamily, platformKey) {
		const mobileData = this.data.mobile[osFamily]
		let devices = []

		// Get devices based on platform type
		if (platformKey === 'simulators' && mobileData.simulators) {
			devices = mobileData.simulators
		} else if (platformKey === 'emulators' && mobileData.emulators) {
			devices = mobileData.emulators
		} else if (platformKey === 'physical' && mobileData.physical) {
			devices = mobileData.physical
		}

		if (!devices.length) return ''

		// Group devices by version and sort versions in descending order
		const devicesByVersion = {}
		devices.forEach(device => {
			if (!devicesByVersion[device.version]) {
				devicesByVersion[device.version] = []
			}
			devicesByVersion[device.version].push(device)
		})

		// Sort versions in descending order (most recent first)
		const sortedVersions = Object.keys(devicesByVersion).sort((a, b) => {
			// Convert versions to numbers for proper comparison
			const aNum = parseFloat(a)
			const bNum = parseFloat(b)
			return bNum - aNum
		})

		return sortedVersions.map(version => {
			const devicesForVersion = devicesByVersion[version]
			const visibleDevices = devicesForVersion.slice(0, 5)
			const remainingCount = devicesForVersion.length - visibleDevices.length

			return `
        <div class="browser-section mb-3" data-version="${version}">
          <div class="sticky top-0 z-10 flex justify-between items-center mb-2 bg-gray-100 dark:bg-gray-800 p-2">
            <div class="flex items-center space-x-2">
              <span class="font-medium">${osFamily === 'ios' ? 'iOS' : 'Android'} ${version}</span>
            </div>
            ${remainingCount > 0 ? `<span class="text-sm text-blue-600 cursor-pointer show-more" data-version="${version}" data-expanded="false">${remainingCount} more ↓</span>` : ''}
          </div>
          <div class="devices-container grid gap-2 grid-cols-2" data-version="${version}">
            ${this.renderMobileDeviceButtons(version, visibleDevices)}
          </div>
        </div>
      `
		}).join('')
	}

	renderMobileDeviceButtons(version, devices) {
		return devices.map(device => {
			const shouldSelect = this.shouldSelectDevice(device.name, version)

			return `
        <span class="px-3 py-2 ${shouldSelect ? BUTTON_SELECTED.join(' ') : 'hover:bg-gray-100 dark:hover:bg-gray-600'} rounded-lg cursor-pointer device-button text-sm"
              data-device-name="${device.name}"
              data-device-version="${version}">
          ${device.name}
        </span>
      `
		}).join('')
	}

	renderVersions(browserKey, versions) {
		return versions.map((version) => {
			// Check if this version should be selected based on defaults
			const shouldSelect = this.shouldSelectVersion(browserKey, version)

			return `
        <span class="px-4 py-1 ${shouldSelect ? BUTTON_SELECTED.join(' ') : 'hover:bg-gray-100 dark:hover:bg-gray-600'} rounded-lg cursor-pointer version-button"
              data-browser="${browserKey}"
              data-version="${version}">
          ${version}
        </span>
      `
		}).join('')
	}

	attachEventListeners() {
		// OS Family selection
		this.container.addEventListener('click', (e) => {
			const osItem = e.target.closest('[data-os-family]')
			if (osItem) {
				this.selectOSFamily(osItem.dataset.osFamily)
				return
			}

			const platformItem = e.target.closest('[data-platform]')
			if (platformItem) {
				this.selectPlatform(platformItem.dataset.platform)
				return
			}

			const showMoreBtn = e.target.closest('.show-more')
			if (showMoreBtn) {
				// Handle both browser versions and mobile device versions
				if (showMoreBtn.dataset.browser) {
					this.toggleVersions(showMoreBtn.dataset.browser)
				} else if (showMoreBtn.dataset.version) {
					this.toggleMobileDevices(showMoreBtn.dataset.version)
				}
				return
			}

			const versionBtn = e.target.closest('.version-button')
			if (versionBtn) {
				this.clickVersion(
					versionBtn.dataset.browser,
					versionBtn.dataset.version
				)
				return
			}

			const deviceBtn = e.target.closest('.device-button')
			if (deviceBtn) {
				this.clickMobileDevice(
					deviceBtn.dataset.deviceName,
					deviceBtn.dataset.deviceVersion
				)
				return
			}
		})
	}

	selectOSFamily(osFamily) {
		// Check if the same OS family is already selected
		// if (this.selectedOS === osFamily) {
		// 	return // Don't reset if clicking the same OS
		// }

		// Store current selection before switching
		const previousBrowser = this.selectedBrowser
		const previousVersion = this.getSelectedVersion()

		// Update UI selection
		this.container.querySelectorAll('[data-os-family]').forEach(item => {
			item.classList.remove(...ITEM_SELECTED)
			item.classList.add(...OS_ITEM_HOVER)
			item.querySelector('.right-arrow').classList.add('hidden')
		})

		const selectedItem = this.container.querySelector(`[data-os-family="${osFamily}"]`)
		if (selectedItem) {
			selectedItem.classList.add(...ITEM_SELECTED)
			selectedItem.classList.remove(...OS_ITEM_HOVER)
			selectedItem.querySelector('.right-arrow').classList.remove('hidden')
		}

		this.selectedOS = osFamily

		// Show platforms panel
		const platformsPanel = this.container.querySelector('#platforms-panel')
		const platformsList = this.container.querySelector('#platforms-list')
		const browsersPanel = this.container.querySelector('#browsers-panel')

		platformsPanel.style.display = 'block'
		platformsList.innerHTML = this.renderPlatforms(osFamily)
		browsersPanel.style.display = 'none'

		// Try to maintain browser selection when switching OS families
		if (previousBrowser) {
			this.tryToMaintainBrowserSelection(osFamily, previousBrowser, previousVersion)
		}
	}

	selectPlatform(platformKey) {
		// Check if the same platform is already selected
		// if (this.selectedPlatform === platformKey) {
		// 	return // Don't reset if clicking the same platform
		// }

		// Store current browser selection before switching
		const previousBrowser = this.selectedBrowser
		const previousVersion = this.getSelectedVersion()

		// Update UI selection
		this.container.querySelectorAll('[data-platform]').forEach(item => {
			item.classList.remove(...ITEM_SELECTED)
			item.classList.add(...PLATFORM_ITEM_HOVER)
			item.querySelector('.right-arrow').classList.add('hidden')
		})

		const selectedItem = this.container.querySelector(`[data-platform="${platformKey}"]`)
		if (selectedItem) {
			selectedItem.classList.add(...ITEM_SELECTED)
			selectedItem.classList.remove(...PLATFORM_ITEM_HOVER)
			selectedItem.querySelector('.right-arrow').classList.remove('hidden')
		}

		this.selectedPlatform = platformKey
		this.selectedBrowser = null

		// Show browsers panel
		const browsersPanel = this.container.querySelector('#browsers-panel')
		const browsersContent = this.container.querySelector('#browsers-content')

		browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, platformKey)
		browsersPanel.style.display = 'block'

		// Try to maintain browser selection when switching platforms
		if (previousBrowser) {
			this.tryToMaintainBrowserSelectionForPlatform(platformKey, previousBrowser, previousVersion)
		}
	}

	toggleVersions(browserKey) {
		const platform = this.data.desktop[this.selectedOS][this.selectedPlatform]
		const browser = platform.browsers[browserKey]
		const versions = browser.versions || []

		const versionsContainer = this.container.querySelector(`[data-browser="${browserKey}"].versions-container`)
		const showMoreBtn = this.container.querySelector(`[data-browser="${browserKey}"].show-more`)

		if (versionsContainer && showMoreBtn) {
			const isExpanded = showMoreBtn.dataset.expanded === 'true'

			if (isExpanded) {
				// Show less - only first 5 versions
				const visibleVersions = versions.slice(0, 5)
				const remainingCount = versions.length - visibleVersions.length
				versionsContainer.innerHTML = this.renderVersions(browserKey, visibleVersions)
				showMoreBtn.textContent = `${remainingCount} more ↓`
				showMoreBtn.dataset.expanded = 'false'
			} else {
				// Show all versions
				versionsContainer.innerHTML = this.renderVersions(browserKey, versions)
				showMoreBtn.textContent = 'show less ↑'
				showMoreBtn.dataset.expanded = 'true'
			}
		}
	}

	toggleMobileDevices(version) {
		const mobileData = this.data.mobile[this.selectedOS]
		let devices = []

		// Get devices based on platform type
		if (this.selectedPlatform === 'simulators' && mobileData.simulators) {
			devices = mobileData.simulators.filter(d => d.version === version)
		} else if (this.selectedPlatform === 'emulators' && mobileData.emulators) {
			devices = mobileData.emulators.filter(d => d.version === version)
		} else if (this.selectedPlatform === 'physical' && mobileData.physical) {
			devices = mobileData.physical.filter(d => d.version === version)
		}

		const devicesContainer = this.container.querySelector(`[data-version="${version}"].devices-container`)
		const showMoreBtn = this.container.querySelector(`[data-version="${version}"].show-more`)

		if (devicesContainer && showMoreBtn) {
			const isExpanded = showMoreBtn.dataset.expanded === 'true'

			if (isExpanded) {
				// Show less - only first 5 devices
				const visibleDevices = devices.slice(0, 5)
				const remainingCount = devices.length - visibleDevices.length
				devicesContainer.innerHTML = this.renderMobileDeviceButtons(version, visibleDevices)
				showMoreBtn.textContent = `${remainingCount} more ↓`
				showMoreBtn.dataset.expanded = 'false'
			} else {
				// Show all devices
				devicesContainer.innerHTML = this.renderMobileDeviceButtons(version, devices)
				showMoreBtn.textContent = 'show less ↑'
				showMoreBtn.dataset.expanded = 'true'
			}
		}
	}

	clickVersion (browserKey, version) {
		this.selectVersion(browserKey, version)

		// Get platform name from the data
		const platformName = this.data.desktop[this.selectedOS][this.selectedPlatform].name
		const browserIconUrl = this.data.desktop[this.selectedOS][this.selectedPlatform].browsers[browserKey].icon

		// Call the click handler if provided
		if (this.clickHandler) {
			this.clickHandler({
				osFamily: this.selectedOS,
				osName: platformName,
				browser: browserKey,
				browserVersion: version,
				browserIconUrl: browserIconUrl
			})
		}

		this.hide()

		// For debugging - remove in production
		console.log('Selection:', {
			osFamily: this.selectedOS,
			osName: platformName,
			browser: browserKey,
			browserVersion: version
		})
	}

	selectVersion(browserKey, version) {
		// Clear all version selections across all browsers
		this.container.querySelectorAll('.version-button').forEach(btn => {
			btn.classList.remove(...BUTTON_SELECTED)
		})

		// Select the clicked version
		const selectedBtn = this.container.querySelector(`[data-browser="${browserKey}"][data-version="${version}"]`)
		if (selectedBtn) {
			selectedBtn.classList.add(...BUTTON_SELECTED)
		}

		this.selectedBrowser = browserKey
		this.selectedVersion = version
	}

	clickMobileDevice (deviceName, deviceVersion) {
		this.selectMobileDevice(deviceName, deviceVersion)

		const platformName = this.selectedPlatform
        
		// Call the click handler if provided
		if (this.clickHandler) {
			this.clickHandler({
				osFamily: this.selectedOS,
				osName: platformName,
				deviceName: deviceName,
				deviceVersion: deviceVersion,
				isRealDevice: this.selectedPlatform === 'physical'
			})
		}

		this.hide()

		// For debugging - remove in production
		console.log('Mobile Selection:', {
			osFamily: this.selectedOS,
			osName: platformName,
			deviceName: deviceName,
			deviceVersion: deviceVersion,
			isRealDevice: this.selectedPlatform === 'physical'
		})
	}

	selectMobileDevice(deviceName, deviceVersion) {
		// Clear all device selections
		this.container.querySelectorAll('.device-button').forEach(btn => {
			btn.classList.remove(...BUTTON_SELECTED)
		})

		// Select the clicked device
		const selectedBtn = this.container.querySelector(`[data-device-name="${deviceName}"][data-device-version="${deviceVersion}"]`)
		if (selectedBtn) {
			selectedBtn.classList.add(...BUTTON_SELECTED)
		}

		this.selectedBrowser = deviceName
		this.selectedVersion = deviceVersion
	}

	// Public method to set click handler
	setClickHandler(handler) {
		this.clickHandler = handler
	}

	// Public method to get current selection
	getSelection() {
		if (!this.selectedOS || !this.selectedPlatform || !this.selectedBrowser) {
			return null
		}

		// Handle desktop platforms
		if (this.data.desktop[this.selectedOS]) {
			const platformName = this.data.desktop[this.selectedOS][this.selectedPlatform].name
			return {
				osFamily: this.selectedOS,
				osName: platformName,
				browser: this.selectedBrowser,
				browserVersion: this.getSelectedVersion()
			}
		}

		// Handle mobile platforms
		if (this.data.mobile && this.data.mobile[this.selectedOS]) {
			const platformTypes = {
				'simulators': 'iOS Simulators',
				'emulators': 'Android Emulators',
				'physical': 'Physical Devices'
			}
			const platformName = platformTypes[this.selectedPlatform] || this.selectedPlatform

			return {
				osFamily: this.selectedOS,
				osName: platformName,
				deviceName: this.selectedBrowser,
				deviceVersion: this.selectedVersion
			}
		}

		return null
	}

	getSelectedVersion() {
		// Check for desktop browser version selection
		const selectedVersionBtn = this.container.querySelector('.version-button.bg-blue-100')
		if (selectedVersionBtn) {
			return selectedVersionBtn.dataset.version
		}

		// Check for mobile device version selection
		const selectedDeviceBtn = this.container.querySelector('.device-button.bg-blue-100')
		if (selectedDeviceBtn) {
			return selectedDeviceBtn.dataset.deviceVersion
		}

		return null
	}

	// Helper method to determine if a version should be selected
	shouldSelectVersion(browserKey, version) {
		if (!this.selectedOS || !this.selectedPlatform || !this.selectedBrowser) {
			return false
		}

		// Only highlight versions for the currently selected browser
		if (browserKey !== this.selectedBrowser) {
			return false
		}

		// Check if this version matches the currently selected version
		return this.selectedVersion === version
	}

	// Helper method to determine if a device should be selected
	shouldSelectDevice(deviceName, deviceVersion) {
		if (!this.selectedOS || !this.selectedPlatform) {
			return false
		}

		// Check if this device matches the currently selected device
		return this.selectedBrowser === deviceName && this.selectedVersion === deviceVersion
	}

	// Find platform key by platform name
	findPlatformKeyByName(osFamily, osName) {
		// Handle desktop platforms
		if (this.data.desktop && this.data.desktop[osFamily]) {
			const platforms = this.data.desktop[osFamily]
			for (const [key, platform] of Object.entries(platforms)) {
				if (platform.name.toLowerCase() === osName.toLowerCase()) {
					return key
				}
			}
		}

		// Handle mobile platforms
		if (this.data.mobile && this.data.mobile[osFamily]) {
			const platformTypes = {
				'iOS Simulators': 'simulators',
				'Android Emulators': 'emulators',
				'Physical Devices': 'physical'
			}
			return platformTypes[osName] || null
		}

		return null
	}

	// Try to maintain browser selection when switching platforms within the same OS
	tryToMaintainBrowserSelectionForPlatform(platformKey, previousBrowser, previousVersion) {
		// Handle desktop platforms
		if (this.data.desktop && this.data.desktop[this.selectedOS]) {
			const platform = this.data.desktop[this.selectedOS][platformKey]
			if (!platform || !platform.browsers) return

			let targetBrowser = null
			let targetVersion = null

			// First priority: same browser + same version
			if (platform.browsers[previousBrowser]) {
				const browser = platform.browsers[previousBrowser]
				const versions = browser.versions || []

				if (versions.includes(previousVersion)) {
					targetBrowser = previousBrowser
					targetVersion = previousVersion
				} else if (versions.length > 0) {
					// Second priority: same browser + latest version
					targetBrowser = previousBrowser
					targetVersion = versions[0] // Latest version
				}
			}

			// Third priority: first available browser with latest version
			if (!targetBrowser) {
				const browserKeys = Object.keys(platform.browsers)
				if (browserKeys.length > 0) {
					const firstBrowserKey = browserKeys[0]
					const browser = platform.browsers[firstBrowserKey]
					const versions = browser.versions || []

					if (versions.length > 0) {
						targetBrowser = firstBrowserKey
						targetVersion = versions[0]
					}
				}
			}

			// Apply the selection if we found something
			if (targetBrowser && targetVersion) {
				this.selectedBrowser = targetBrowser
				this.selectedVersion = targetVersion

				// Re-render browsers panel to show the selection
				const browsersContent = this.container.querySelector('#browsers-content')
				if (browsersContent) {
					browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, this.selectedPlatform)
				}

				this.selectVersion(targetBrowser, targetVersion)

				// Check if the browserVersion is in the first 5 visible versions
				const browser = platform.browsers[targetBrowser]
				const versions = browser.versions || []
				const versionIndex = versions.indexOf(targetVersion)

				// If version exists but is not in the first 5 visible versions, expand the list
				if (versionIndex !== -1 && versionIndex >= 5) {
					// Find the show-more button for this browser and expand it
					const showMoreBtn = this.container.querySelector(`[data-browser="${targetBrowser}"].show-more`)
					if (showMoreBtn && showMoreBtn.dataset.expanded !== 'true') {
						// Expand the versions by calling toggleVersions
						this.toggleVersions(targetBrowser)
					}
				}
			}
		}

		// Handle mobile platforms - try to maintain device selection
		if (this.data.mobile && this.data.mobile[this.selectedOS]) {
			const mobileData = this.data.mobile[this.selectedOS]
			let devices = []

			// Get devices for the new platform
			if (platformKey === 'simulators' && mobileData.simulators) {
				devices = mobileData.simulators
			} else if (platformKey === 'emulators' && mobileData.emulators) {
				devices = mobileData.emulators
			} else if (platformKey === 'physical' && mobileData.physical) {
				devices = mobileData.physical
			}

			if (devices.length > 0) {
				let targetDevice = null

				// First priority: same device name + same version
				targetDevice = devices.find(d => d.name === previousBrowser && d.version === previousVersion)

				// Second priority: same device name with any version
				if (!targetDevice) {
					targetDevice = devices.find(d => d.name === previousBrowser)
				}

				// Third priority: first available device (sorted by most recent version)
				if (!targetDevice) {
					const sortedDevices = devices.sort((a, b) => parseFloat(b.version) - parseFloat(a.version))
					targetDevice = sortedDevices[0]
				}

				if (targetDevice) {
					this.selectedBrowser = targetDevice.name
					this.selectedVersion = targetDevice.version

					// Re-render browsers panel to show the selection
					const browsersContent = this.container.querySelector('#browsers-content')
					if (browsersContent) {
						browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, this.selectedPlatform)
					}

					this.selectMobileDevice(targetDevice.name, targetDevice.version)
				}
			}
		}
	}

	// Try to maintain browser selection when switching OS families
	tryToMaintainBrowserSelection(osFamily, previousBrowser, previousVersion) {
		// Handle desktop platforms
		if (this.data.desktop && this.data.desktop[osFamily]) {
			const platforms = this.data.desktop[osFamily]
			let targetPlatform = null
			let targetBrowser = null
			let targetVersion = null

			// Look for the exact browser + version combination
			for (const [platformKey, platform] of Object.entries(platforms)) {
				if (platform.browsers && platform.browsers[previousBrowser]) {
					const browser = platform.browsers[previousBrowser]
					const versions = browser.versions || []

					// First priority: same browser + same version
					if (versions.includes(previousVersion)) {
						targetPlatform = platformKey
						targetBrowser = previousBrowser
						targetVersion = previousVersion
						break
					}

					// Second priority: same browser + latest version (if we haven't found anything better)
					if (!targetPlatform && versions.length > 0) {
						targetPlatform = platformKey
						targetBrowser = previousBrowser
						targetVersion = versions[0] // Latest version
					}
				}
			}

			// Third priority: first available browser with latest version
			if (!targetPlatform) {
				for (const [platformKey, platform] of Object.entries(platforms)) {
					if (platform.browsers) {
						const browserKeys = Object.keys(platform.browsers)
						if (browserKeys.length > 0) {
							const firstBrowserKey = browserKeys[0]
							const browser = platform.browsers[firstBrowserKey]
							const versions = browser.versions || []

							if (versions.length > 0) {
								targetPlatform = platformKey
								targetBrowser = firstBrowserKey
								targetVersion = versions[0]
								break
							}
						}
					}
				}
			}

			// Apply the selection if we found something
			if (targetPlatform && targetBrowser && targetVersion) {
				this.selectPlatform(targetPlatform)
				this.selectedBrowser = targetBrowser
				this.selectedVersion = targetVersion

				// Re-render browsers panel to show the selection
				const browsersContent = this.container.querySelector('#browsers-content')
				if (browsersContent) {
					browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, this.selectedPlatform)
				}

				// Check if the browserVersion is in the first 5 visible versions
				const platform = this.data.desktop[this.selectedOS][this.selectedPlatform]
				const browser = platform.browsers[targetBrowser]
				const versions = browser.versions || []
				const versionIndex = versions.indexOf(targetVersion)

				// If version exists but is not in the first 5 visible versions, expand the list
				if (versionIndex !== -1 && versionIndex >= 5) {
					// Find the show-more button for this browser and expand it
					const showMoreBtn = this.container.querySelector(`[data-browser="${targetBrowser}"].show-more`)
					if (showMoreBtn && showMoreBtn.dataset.expanded !== 'true') {
						// Expand the versions by calling toggleVersions
						this.toggleVersions(targetBrowser)
					}
				}
			}
		}

		// Handle mobile platforms
		if (this.data.mobile && this.data.mobile[osFamily]) {
			const mobileData = this.data.mobile[osFamily]
			let targetPlatform = null
			let targetDevice = null

			// Check all available platform types for mobile
			const platformTypes = ['simulators', 'emulators', 'physical']

			for (const platformType of platformTypes) {
				if (mobileData[platformType]) {
					const devices = mobileData[platformType]

					// First priority: same device name + same version
					let device = devices.find(d => d.name === previousBrowser && d.version === previousVersion)

					// Second priority: same device name with any version
					if (!device) {
						device = devices.find(d => d.name === previousBrowser)
					}

					if (device) {
						targetPlatform = platformType
						targetDevice = device
						break
					}
				}
			}

			// Third priority: first available device from any platform (prefer physical, then simulators/emulators)
			if (!targetPlatform) {
				const preferredOrder = ['physical', 'simulators', 'emulators']
				for (const platformType of preferredOrder) {
					if (mobileData[platformType] && mobileData[platformType].length > 0) {
						const devices = mobileData[platformType]
						const sortedDevices = devices.sort((a, b) => parseFloat(b.version) - parseFloat(a.version))
						targetPlatform = platformType
						targetDevice = sortedDevices[0]
						break
					}
				}
			}

			// Apply the selection if we found something
			if (targetPlatform && targetDevice) {
				this.selectPlatform(targetPlatform)
				this.selectedBrowser = targetDevice.name
				this.selectedVersion = targetDevice.version

				// Re-render browsers panel to show the selection
				const browsersContent = this.container.querySelector('#browsers-content')
				if (browsersContent) {
					browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, this.selectedPlatform)
				}

				this.selectMobileDevice(targetDevice.name, targetDevice.version)
			}
		}
	}

	// Apply initial selection after data is loaded
	applyDefaultSelection(defaultSelection) {
		if (!this.data) return

		// Find and select the initial OS family
		const osFamily = defaultSelection.osFamily
		this.selectedOS = osFamily

		// Handle desktop platforms
		if (this.data.desktop && this.data.desktop[osFamily]) {
			// Find and select the initial platform
			const platformKey = this.findPlatformKeyByName(osFamily, defaultSelection.osName)
			if (platformKey) {
				this.selectedPlatform = platformKey

				// Find and select the initial browser and version
				const platform = this.data.desktop[osFamily][platformKey]
				if (platform && platform.browsers && platform.browsers[defaultSelection.browserName]) {
					const browser = platform.browsers[defaultSelection.browserName]
					const versions = browser.versions || []

					if (versions.length > 0) {
						// Use specific version or first one
						const targetVersion = defaultSelection.browserVersion || versions[0]
						this.selectedBrowser = defaultSelection.browserName
						this.selectedVersion = targetVersion
					}
				}
			}
		}

		// Handle mobile platforms
		if (this.data.mobile && this.data.mobile[osFamily]) {
			// Find and select the initial platform
			const platformKey = this.findPlatformKeyByName(osFamily, defaultSelection.osName)
			if (platformKey) {
				this.selectedPlatform = platformKey

				// Find and select the initial device
				if (defaultSelection.deviceName && defaultSelection.deviceVersion) {
					this.selectedBrowser = defaultSelection.deviceName
					this.selectedVersion = defaultSelection.deviceVersion
				} else {
					// Select first available device
					const mobileData = this.data.mobile[osFamily]
					let devices = []

					if (platformKey === 'simulators' && mobileData.simulators) {
						devices = mobileData.simulators
					} else if (platformKey === 'emulators' && mobileData.emulators) {
						devices = mobileData.emulators
					} else if (platformKey === 'physical' && mobileData.physical) {
						devices = mobileData.physical
					}

					if (devices.length > 0) {
						// Sort by version to get the most recent
						const sortedDevices = devices.sort((a, b) => parseFloat(b.version) - parseFloat(a.version))
						this.selectedBrowser = sortedDevices[0].name
						this.selectedVersion = sortedDevices[0].version
					}
				}
			}
		}

		// Update UI selection (common for both desktop and mobile)
		this.container.querySelectorAll('[data-os-family]').forEach(item => {
			item.classList.remove(...ITEM_SELECTED)
			item.classList.add(...OS_ITEM_HOVER)
			item.querySelector('.right-arrow').classList.add('hidden')
		})

		const selectedItem = this.container.querySelector(`[data-os-family="${osFamily}"]`)
		if (selectedItem) {
			selectedItem.classList.add(...ITEM_SELECTED)
			selectedItem.classList.remove(...OS_ITEM_HOVER)
			selectedItem.querySelector('.right-arrow').classList.remove('hidden')
		}

		// Show platforms panel
		const platformsPanel = this.container.querySelector('#platforms-panel')
		const platformsList = this.container.querySelector('#platforms-list')
		const browsersPanel = this.container.querySelector('#browsers-panel')
		const browsersContent = this.container.querySelector('#browsers-content')

		if (this.selectedPlatform) {
			platformsList.innerHTML = this.renderPlatforms(osFamily)
			platformsPanel.style.display = 'block'
			browsersContent.innerHTML = this.renderBrowsers(this.selectedOS, this.selectedPlatform)
			browsersPanel.style.display = 'block'

			// Handle version visibility for desktop browsers
			if (this.data.desktop && this.data.desktop[osFamily]) {
				this.ensureVersionVisibility(defaultSelection.browserName, defaultSelection.browserVersion)
				this.scrollVersionIntoView(defaultSelection.browserName, defaultSelection.browserVersion)
			}
		}
	}

	// Ensure that a specific browser version is visible by expanding the version list if necessary
	ensureVersionVisibility(browserName, browserVersion) {
		if (!browserName || !browserVersion || !this.selectedOS || !this.selectedPlatform) {
			return
		}

		// Handle desktop browsers
		if (this.data.desktop && this.data.desktop[this.selectedOS]) {
			const platform = this.data.desktop[this.selectedOS][this.selectedPlatform]
			if (!platform || !platform.browsers || !platform.browsers[browserName]) {
				return
			}

			const browser = platform.browsers[browserName]
			const versions = browser.versions || []

			// Check if the browserVersion is in the first 5 visible versions
			const versionIndex = versions.indexOf(browserVersion)

			// If version exists but is not in the first 5 visible versions, expand the list
			if (versionIndex !== -1 && versionIndex >= 5) {
				// Find the show-more button for this browser and expand it
				const showMoreBtn = this.container.querySelector(`[data-browser="${browserName}"].show-more`)
				if (showMoreBtn && showMoreBtn.dataset.expanded !== 'true') {
					// Expand the versions by calling toggleVersions
					this.toggleVersions(browserName)
				}
			}
		}

		// Handle mobile devices - check if device needs to be expanded in version group
		if (this.data.mobile && this.data.mobile[this.selectedOS]) {
			const mobileData = this.data.mobile[this.selectedOS]
			let devices = []

			// Get devices based on platform type
			if (this.selectedPlatform === 'simulators' && mobileData.simulators) {
				devices = mobileData.simulators
			} else if (this.selectedPlatform === 'emulators' && mobileData.emulators) {
				devices = mobileData.emulators
			} else if (this.selectedPlatform === 'physical' && mobileData.physical) {
				devices = mobileData.physical
			}

			// Find devices with the specific version
			const devicesForVersion = devices.filter(d => d.version === browserVersion)
			if (devicesForVersion.length > 0) {
				// Check if the device is in the first 5 visible devices for this version
				const deviceIndex = devicesForVersion.findIndex(d => d.name === browserName)

				// If device exists but is not in the first 5 visible devices, expand the list
				if (deviceIndex !== -1 && deviceIndex >= 5) {
					// Find the show-more button for this version and expand it
					const showMoreBtn = this.container.querySelector(`[data-version="${browserVersion}"].show-more`)
					if (showMoreBtn && showMoreBtn.dataset.expanded !== 'true') {
						// Expand the devices by calling toggleMobileDevices
						this.toggleMobileDevices(browserVersion)
					}
				}
			}
		}
	}

	// Scroll the selected version button into view within the browsers panel
	scrollVersionIntoView(browserName, browserVersion) {
		if (!browserName || !browserVersion) {
			return
		}

		// Use setTimeout to ensure DOM has been updated after expansion
		setTimeout(() => {
			let selectedBtn = null

			// Handle desktop browsers
			if (this.data.desktop && this.data.desktop[this.selectedOS]) {
				selectedBtn = this.container.querySelector(`[data-browser="${browserName}"][data-version="${browserVersion}"]`)
			}

			// Handle mobile devices
			if (this.data.mobile && this.data.mobile[this.selectedOS]) {
				selectedBtn = this.container.querySelector(`[data-device-name="${browserName}"][data-device-version="${browserVersion}"]`)
			}

			const browsersPanel = this.container.querySelector('#browsers-panel')

			if (selectedBtn && browsersPanel) {
				// Get the bounds of the selected button relative to the browsers panel
				const buttonRect = selectedBtn.getBoundingClientRect()
				const panelRect = browsersPanel.getBoundingClientRect()

				// Check if the button is below the visible area
				if (buttonRect.bottom > panelRect.bottom) {
					// Scroll the button into view with some padding
					const scrollTop = browsersPanel.scrollTop + (buttonRect.bottom - panelRect.bottom) + 20
					browsersPanel.scrollTop = scrollTop
				}
				// Check if the button is above the visible area (less common but possible)
				else if (buttonRect.top < panelRect.top) {
					const scrollTop = browsersPanel.scrollTop - (panelRect.top - buttonRect.top) - 20
					browsersPanel.scrollTop = scrollTop
				}
			}
		}, 100) // Small delay to ensure DOM updates are complete
	}
}
